/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Hightouch MCP", description = "Connect your AI assistant directly to your marketing stack to build audiences, design journeys, generate creatives, and analyze performance in plain language.", lastUpdatedDate = new Date(1786579200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Hightouch MCP",
        description: "Connect your AI assistant directly to your marketing stack to build audiences, design journeys, generate creatives, and analyze performance in plain language.",
        lastUpdatedDate: "Thu Aug 13 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "What you can do",
          "depth": 0,
          "id": "what-you-can-do"
        }, {
          "value": "How it works",
          "depth": 0,
          "id": "how-it-works"
        }, {
          "value": "Why use the Hightouch MCP",
          "depth": 0,
          "id": "why-use-the-hightouch-mcp"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      em: "em"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "The Hightouch MCP",
        contactMessage: "Hi, I'm interested in getting access to the Hightouch MCP."
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch MCP server connects your AI assistant directly to your marketing stack so your team can build audiences, manage syncs, design journeys, generate emails and ads, and analyze performance — all in plain language."
      }), "\n", _jsx(_components.h2, {
        id: "what-you-can-do",
        children: "What you can do"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All customers:"
          }), " Understand your customer schema, build audiences, create and analyze journeys, manage syncs, and create syncs from templates. Run marketing data analysis with Hightouch's AI agents."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lifecycle Studio Customers:"
          }), " Build and iterate on email and SMS content."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad Studio Customers:"
          }), " Generate visual ad creatives from a prompt, refine them with follow-up instructions, and browse existing projects."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "Connect to Hightouch's MCP from Claude, ChatGPT, or another AI assistant you already use, and build audiences, draft campaigns, or analyze performance through conversation. Your assistant uses Hightouch's MCP as needed to gather information and take action, and you can see results in real time, iterate, and save when you're happy with the outcome."
      }), "\n", _jsx(_components.p, {
        children: "Example use cases:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.em, {
            children: "\"Create an audience of customers that added an item to their cart in the last day but haven't completed a transaction, then create a sync to Braze.\""
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.em, {
            children: "\"Show me which email campaigns drove the most conversions last quarter, and how did they perform compared to the quarter before?\""
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.em, {
            children: "\"Create creatives for our new card promo targeted towards working adults in high-net-worth households.\""
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "why-use-the-hightouch-mcp",
        children: "Why use the Hightouch MCP"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Work in your existing flow and across your stack."
        }), " If your team already works in Claude or another AI assistant, Hightouch is now accessible from that same interface. Because your assistant can connect to other tools simultaneously, you can orchestrate entire workflows in one conversation — get insights from Hightouch, draft a report, and send it to your team on Slack without switching tabs."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Gives your assistant the context it needs."
        }), " The MCP server connects you to Hightouch's agents, which have all the relevant skills and context about your business and data to build the right audiences, journeys, and campaigns out of the box, without you having to re-explain your business context every time."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Built for action."
        }), " Your assistant can create and save audiences, design journeys, and generate creatives, not just describe what's possible."]
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Is my data safe?"
        }), "\nYes. All operations are scoped to your authenticated Hightouch workspace and respect your existing role-based access controls."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Can the MCP take actions in my workspace, or is it read-only?"
        }), "\nBoth. Your assistant can read your schema and explore your data, and also take actions — like creating and saving audiences, designing journeys, and generating creatives."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Do we charge for MCP access?"
        }), "\nNo. Access to the MCP is no additional cost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Do I need to be technical to use this?"
        }), "\nNo. The MCP is designed for marketers. You interact in plain English, and the assistant handles the underlying logic."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Which AI client should I use?"
        }), "\nAny AI client that supports the MCP standard can connect to Hightouch — including Claude, ChatGPT, Gemini Enterprise, Microsoft Copilot, and more."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
